import React, { useState } from "react";
import PropTypes from "prop-types";
/* Components */
import { Container, Row, Col } from "react-bootstrap";
import CustomTooltip from "../../CommonForBoth/CustomTooltip";
import ETokenRowDesktop from "./ETokenRowDesktop";
import ETokenRowMobile from "./ETokenRowMobile";
import SrETokenCard from "./SrETokenCard";
/* Helpers and Selectors */
import { checkVisibility, isJrOfSrFilter, isSrPool } from "../../../helpers/ensuro_helper";
/* i18n */
import { withTranslation, Trans } from "react-i18next";
import "./../style.scss";
import { docs } from "../../../config";

const ETokens = ({ t, eTokens, state, mode }) => {
  const srPools = eTokens.filter((eToken) => isSrPool(eToken) && checkVisibility(eToken, mode));
  const [srEtkFilter, setSrEtkFilter] = useState(srPools.map((etk) => etk.address));
  const [jrPools, setJrPools] = useState(eTokens);

  const handleLinkClick = (e) => {
    e.stopPropagation();
  };

  const DescriptionWithLink = ({ children }) => (
    <Trans>
      {children}
      <a
        href={docs.eTokenRisks.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#007bff" }}
        onClick={handleLinkClick}
      >
        {" "}
        More details
      </a>
    </Trans>
  );

  const poolBadges = [
    { key: "kyc", title: t("KYC"), style: "primary", description: t("KYCBadgeTooltip") },
    { key: "restricted", title: t("Restricted"), style: "secondary", description: t("RestrictedBadgeTooltip") },
    { key: "oversold", title: t("Oversold"), style: "danger", description: t("OversoldBadgeTooltip") },
    {
      key: "AAA",
      title: "AAA",
      style: "low-risk",
      description: (
        <DescriptionWithLink>Minimal Risk. Probability of Significant Loss between 0% and 9%.</DescriptionWithLink>
      ),
    },
    {
      key: "AA",
      title: "AA",
      style: "low-risk",
      description: (
        <DescriptionWithLink>Very Low Risk. Probability of Significant Loss between 10% and 19%.</DescriptionWithLink>
      ),
    },
    {
      key: "A",
      title: "A",
      style: "low-risk",
      description: (
        <DescriptionWithLink>Low Risk. Probability of Significant Loss between 20% and 29%.</DescriptionWithLink>
      ),
    },
    {
      key: "BBB",
      title: "BBB",
      style: "mid-risk",
      description: (
        <DescriptionWithLink>
          Moderate Risk (Investment Grade). Probability of Significant Loss between 30% and 39%.
        </DescriptionWithLink>
      ),
    },
    {
      key: "BB",
      title: "BB",
      style: "mid-risk",
      description: (
        <DescriptionWithLink>
          Speculative (High Yield). Probability of Significant Loss between 40% and 49%.
        </DescriptionWithLink>
      ),
    },
    {
      key: "B",
      title: "B",
      style: "mid-risk",
      description: (
        <DescriptionWithLink>
          Highly Speculative. Probability of Significant Loss between 50% and 59%.
        </DescriptionWithLink>
      ),
    },
    {
      key: "CCC",
      title: "CCC",
      style: "high-risk",
      description: (
        <DescriptionWithLink>
          Substantial Risk. Probability of Significant Loss between 60% and 69%.
        </DescriptionWithLink>
      ),
    },
    {
      key: "CC",
      title: "CC",
      style: "high-risk",
      description: (
        <DescriptionWithLink>Very High Risk . Probability of Significant Loss between 70% and 79%.</DescriptionWithLink>
      ),
    },
    {
      key: "C",
      title: "C",
      style: "high-risk",
      description: (
        <DescriptionWithLink>
          Extremely High Risk. Probability of Significant Loss between 80% and 89%.
        </DescriptionWithLink>
      ),
    },
    {
      key: "D",
      title: "D",
      style: "high-risk",
      description: (
        <DescriptionWithLink>
          Default Imminent / Highest Risk. Probability of Significant Loss between 90% and 100%.
        </DescriptionWithLink>
      ),
    },
  ];

  const addSrEtkFilter = (eToken) => {
    if (srEtkFilter.indexOf(eToken.address) >= 0) {
      srEtkFilter.splice(srEtkFilter.indexOf(eToken.address), 1);
    } else {
      srEtkFilter.push(eToken.address);
    }
    setSrEtkFilter(srEtkFilter);

    setJrPools(eTokens);
  };

  return (
    <>
      <Row className="flex-nowrap" style={{ overflowX: "auto" }}>
        {srPools.map(
          (eToken, index) =>
            checkVisibility(eToken, mode) && (
              <SrETokenCard
                eToken={eToken}
                key={index}
                poolBadges={poolBadges}
                srEtkFilter={srEtkFilter}
                onCheck={addSrEtkFilter}
                checked={srEtkFilter.includes(eToken.address)}
              />
            )
        )}
      </Row>
      <br />
      <Container className="desktop no-margin">
        <Row className="table-header">
          <Col md={3} className="margin-auto">
            <strong>{t("Name")} </strong>
          </Col>
          <Col md={3} className="margin-auto"></Col>
          <CustomTooltip text={t("LiquidityTooltip")} placement="top">
            <Col md={2} className="margin-auto">
              <strong>{t("Liquidity")} </strong>
              <span className="fa fa-info-circle" data-toggle="tooltip" data-original-title="Info" />
            </Col>
          </CustomTooltip>
          <CustomTooltip text={t("URTooltip")} placement="top">
            <Col md={2} className="margin-auto">
              <strong>{t("Utilization Rate")} </strong>
              <span className="fa fa-info-circle" data-toggle="tooltip" data-original-title="Info" />
            </Col>
          </CustomTooltip>
          <CustomTooltip text={t("APYTooltip")} placement="top">
            <Col md={1} className="margin-auto">
              <strong>{t("APY 30")} </strong>
              <span className="fa fa-info-circle" data-toggle="tooltip" data-original-title="Info" />
            </Col>
          </CustomTooltip>
          <CustomTooltip text={t("APY360Tooltip")} placement="top">
            <Col md={1} className="margin-auto">
              <strong>{t("APY 360")} </strong>
              <span className="fa fa-info-circle" data-toggle="tooltip" data-original-title="Info" />
            </Col>
          </CustomTooltip>
        </Row>
      </Container>
      {jrPools.map(
        (eToken) =>
          checkVisibility(eToken, mode) &&
          isJrOfSrFilter(eToken, srEtkFilter, state.ETokensReducer, mode) &&
          !isSrPool(eToken) && (
            <React.Fragment key={eToken.address}>
              <ETokenRowDesktop idx={eToken.address + "-Desktop"} eToken={eToken} poolBadges={poolBadges} />
              <ETokenRowMobile idx={eToken.address + "-Mobile"} eToken={eToken} poolBadges={poolBadges} />
            </React.Fragment>
          )
      )}
    </>
  );
};

ETokens.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(ETokens);
